import { BaseService } from './BaseService';
import type {
  AgentPerformanceDto,
  CostBreakdownDto,
  CreateDashboardViewRequest,
  CreateDashboardWidgetRequest,
  DashboardMetricsDto,
  DashboardViewDto,
  DashboardWidgetDto,
  RealTimeMetricsDto,
  UpdateDashboardViewRequest,
  UpdateDashboardWidgetRequest,
  UsageTrendDto,
  WidgetLayoutUpdate,
} from '../types/dashboard';

/**
 * Dashboard service for dashboard & analytics endpoints.
 */
export class DashboardService extends BaseService {
  /**
   * Gets the main dashboard metrics snapshot.
   */
  async getMainMetrics(): Promise<DashboardMetricsDto> {
    return this.get<DashboardMetricsDto>('/dashboard/metrics');
  }

  /**
   * Gets usage trends for a date range (UTC).
   */
  async getUsageTrends(startDateUtc?: string, endDateUtc?: string): Promise<UsageTrendDto[]> {
    const queryParams = new URLSearchParams();
    if (startDateUtc) queryParams.append('startDateUtc', startDateUtc);
    if (endDateUtc) queryParams.append('endDateUtc', endDateUtc);
    const url = `/dashboard/usage-trends${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<UsageTrendDto[]>(url);
  }

  /**
   * Gets agent performance metrics for a date range (UTC).
   */
  async getAgentPerformance(startDateUtc?: string, endDateUtc?: string): Promise<AgentPerformanceDto[]> {
    const queryParams = new URLSearchParams();
    if (startDateUtc) queryParams.append('startDateUtc', startDateUtc);
    if (endDateUtc) queryParams.append('endDateUtc', endDateUtc);
    const url = `/dashboard/agent-performance${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<AgentPerformanceDto[]>(url);
  }

  /**
   * Gets a cost breakdown for a date range (UTC).
   */
  async getCostBreakdown(startDateUtc?: string, endDateUtc?: string): Promise<CostBreakdownDto> {
    const queryParams = new URLSearchParams();
    if (startDateUtc) queryParams.append('startDateUtc', startDateUtc);
    if (endDateUtc) queryParams.append('endDateUtc', endDateUtc);
    const url = `/dashboard/cost-breakdown${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<CostBreakdownDto>(url);
  }

  /**
   * Gets near real-time metrics snapshot (HTTP).
   */
  async getRealTimeMetrics(): Promise<RealTimeMetricsDto> {
    return this.get<RealTimeMetricsDto>('/dashboard/real-time');
  }

  /**
   * Gets dashboard views for the current tenant/project.
   */
  async getDashboardViews(): Promise<DashboardViewDto[]> {
    return this.get<DashboardViewDto[]>('/dashboard-views');
  }

  /**
   * Gets a dashboard view by id (includes widgets).
   */
  async getDashboardViewById(id: string): Promise<DashboardViewDto> {
    return this.get<DashboardViewDto>(`/dashboard-views/${id}`);
  }

  /**
   * Creates a dashboard view.
   */
  async createDashboardView(data: CreateDashboardViewRequest): Promise<DashboardViewDto> {
    return this.post<DashboardViewDto>('/dashboard-views', data);
  }

  /**
   * Updates a dashboard view.
   */
  async updateDashboardView(id: string, data: UpdateDashboardViewRequest): Promise<DashboardViewDto> {
    return this.put<DashboardViewDto>(`/dashboard-views/${id}`, data);
  }

  /**
   * Deletes a dashboard view.
   */
  async deleteDashboardView(id: string): Promise<void> {
    return this.delete<void>(`/dashboard-views/${id}`);
  }

  /**
   * Gets widgets for a dashboard view.
   */
  async getDashboardWidgets(dashboardViewId: string): Promise<DashboardWidgetDto[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('dashboardViewId', dashboardViewId);
    return this.get<DashboardWidgetDto[]>(`/dashboard-widgets?${queryParams.toString()}`);
  }

  /**
   * Creates a dashboard widget.
   */
  async createDashboardWidget(data: CreateDashboardWidgetRequest): Promise<DashboardWidgetDto> {
    return this.post<DashboardWidgetDto>('/dashboard-widgets', data);
  }

  /**
   * Updates a dashboard widget.
   */
  async updateDashboardWidget(id: string, data: UpdateDashboardWidgetRequest): Promise<DashboardWidgetDto> {
    return this.put<DashboardWidgetDto>(`/dashboard-widgets/${id}`, data);
  }

  /**
   * Deletes a dashboard widget.
   */
  async deleteDashboardWidget(id: string): Promise<void> {
    return this.delete<void>(`/dashboard-widgets/${id}`);
  }

  /**
   * Reorders/repositions widgets within a dashboard view.
   */
  async reorderWidgets(dashboardViewId: string, updates: WidgetLayoutUpdate[]): Promise<void> {
    return this.post<void>('/dashboard-widgets/reorder', { dashboardViewId, updates });
  }
}

export const dashboardService = new DashboardService();

